// Layer 3 (Transformation) — the Critical controls tab (polish.P20, locked
// comp `risk.html` A, from C): Interlocks and Control verification joined,
// one row per interlock with its latest CV check beside it. The seed is 1:1
// (`CV-0n → IL-0n`); rows keep the interlock register's No-one-first order.
// Pure: no React, no engine mutation.

import { CONTROL_VERIFICATION_CHECKS, type ControlVerificationCheck, type OverrideAuthority } from './seed';
import { formatShiftDate, interlockRegister, type InterlockRow } from './Scene.transform';

/** The check's result as the glass names it. A pass that ran late reads
 *  `Pass · late` from the seed's `onTime: false` — no new state (adjudication 6). */
export type ControlResult = 'pass' | 'pass-late' | 'degraded' | 'fail';

export const CONTROL_RESULT_LABEL: Record<ControlResult, string> = {
  pass: 'Pass',
  'pass-late': 'Pass · late',
  degraded: 'Degraded',
  fail: 'Fail',
};

export function controlResult(check: Pick<ControlVerificationCheck, 'lastResult' | 'onTime'>): ControlResult {
  if (check.lastResult === 'pass') return check.onTime ? 'pass' : 'pass-late';
  return check.lastResult;
}

export type CriticalControlRow = {
  interlock: InterlockRow;
  /** `condition · effect`, the interlock's second line. */
  trigger: string;
  noOneOverride: boolean;
  check?: ControlVerificationCheck & {
    result: ControlResult;
    resultLabel: string;
    checkedLabel: string;
    nextDueLabel: string;
  };
};

export function criticalControls(): readonly CriticalControlRow[] {
  const byInterlock = new Map(CONTROL_VERIFICATION_CHECKS.map((check) => [check.interlockId, check] as const));
  return interlockRegister().map((interlock) => {
    const check = byInterlock.get(interlock.id);
    const result = check ? controlResult(check) : undefined;
    return {
      interlock,
      trigger: `${interlock.condition} · ${interlock.effect}`,
      noOneOverride: interlock.overrideAuthority === ('No one' satisfies OverrideAuthority),
      check: check && result
        ? {
            ...check,
            result,
            resultLabel: CONTROL_RESULT_LABEL[result],
            checkedLabel: formatShiftDate(check.checkedAt),
            nextDueLabel: formatShiftDate(check.nextDue),
          }
        : undefined,
    };
  });
}
